import SiteCoreLinks from "@/components/SiteCoreLinks";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, collectionPageSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, MapPin, Phone, ArrowRight, Building2 } from "lucide-react";
import { locations, regions, type Location } from "@/data/locations";
import { NearMeWidget } from "@/components/NearMeWidget";
import { DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";

export const Route = createFileRoute("/locations/")({
  head: () => {
    const SITE = "https://nyclanguageinstitute.com";
    const TITLE = "พื้นที่ให้บริการ — 50 เขต กทม. + 77 จังหวัด | NYC Language Institute";
    const DESC = "ค้นหาสำนักงานและตัวแทน NYC Legal & Notary Service ทั่วประเทศ ครอบคลุม 50 เขตกรุงเทพฯ และทุกจังหวัดในประเทศไทย — คุยกับผู้เชี่ยวชาญตัวจริง ตอบกลับภายในไม่กี่นาที";
    return {
      meta: [
        { title: ctaTitle(TITLE, "/locations", "th")},
        { name: "description", content: ctaDesc(DESC, "/locations", "th")},
        { property: "og:title", content: ctaTitle(TITLE, "/locations", "th")},
        { property: "og:description", content: ctaDesc(DESC, "/locations", "th")},
        { property: "og:url", content: `${SITE}/locations` },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: `${SITE}/locations` }, ...hreflangLinks("/locations")],
      scripts: [speakableSchema(), consolidateGraph([
        collectionPageSchema({ path: "/locations", name: TITLE, description: DESC, itemCount: locations.length }),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "NYC Legal & Notary Service — พื้นที่ให้บริการทั่วประเทศ",
            url: `${SITE}/locations`,
            numberOfItems: locations.length,
            itemListElement: locations.slice(0, 100).map((l, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${SITE}/locations/${l.slug}`,
              name: l.nameTh,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Locations", item: `${SITE}/locations` },
            ],
          }),
        },
      ])],
    };
  },
  component: LocationsPage,
});

function LocationsPage() {
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState<string>("ทั้งหมด");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return locations.filter((l) => {
      if (region !== "ทั้งหมด" && l.region !== region) return false;
      if (!q) return true;
      return (
        l.nameTh.toLowerCase().includes(q) ||
        l.nameEn.toLowerCase().includes(q) ||
        l.region.toLowerCase().includes(q)
      );
    });
  }, [query, region]);

  const grouped = useMemo(() => {
    const map = new Map<string, Location[]>();
    for (const l of filtered) {
      if (!map.has(l.region)) map.set(l.region, []);
      map.get(l.region)!.push(l);
    }
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <main className="min-h-screen bg-gradient-to-b from-navy-deep via-navy to-navy-deep text-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 px-4 py-16 sm:px-6 sm:py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-gold/80 hover:text-gold">
            ← กลับหน้าแรก
          </Link>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-5xl">
            พื้นที่ให้บริการทั่วประเทศ
          </h1>
          <p className="mt-3 max-w-3xl text-sm text-white/75 sm:text-base">
            ค้นหาสำนักงานสาขาและสำนักงานตัวแทน NYC Legal & Notary Service — ครอบคลุม{" "}
            <strong className="text-gold">50 เขตกรุงเทพมหานคร</strong> และ{" "}
            <strong className="text-gold">77 จังหวัดทั่วประเทศไทย</strong>{" "}
            พร้อมบริการรับ-ส่งเอกสารถึงที่
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <DirectAnswer entity="NYC Language Institute — Service Coverage Thailand">
              NYC Language Institute ให้บริการแปล รับรอง Notary และ Apostille ครอบคลุม 50 เขตกรุงเทพมหานคร และ 77 จังหวัดทั่วประเทศไทย ผ่านสำนักงานใหญ่และเครือข่ายตัวแทน — รับ-ส่งเอกสารถึงที่ในกรุงเทพและปริมณฑล จัดส่งทั่วประเทศผ่าน EMS/Kerry ภายใน 1-2 วัน และรองรับลูกค้าต่างชาติทั่วโลกผ่านบริการออนไลน์ (LINE/Email/Video Notary)
            </DirectAnswer>
            <KeyFactsTable rows={[
              { k: "เขตกรุงเทพฯ", v: "50 เขต" },
              { k: "จังหวัด", v: "77 จังหวัด" },
              { k: "รับ-ส่งถึงที่", v: "กรุงเทพ + ปริมณฑล" },
              { k: "จัดส่งทั่วประเทศ", v: "EMS 1-2 วัน" },
              { k: "บริการออนไลน์", v: "ทั่วโลก 24/7" },
              { k: "DBD", v: "0435567000061" },
            ]} />
          </div>

          {/* Search + Filter */}
          <div className="mt-8 grid gap-3 sm:grid-cols-[1fr_auto]">
            <label className="relative">
              <span className="sr-only">ค้นหาพื้นที่ให้บริการ</span>
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/80" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="ค้นหาเขต / จังหวัด / ภาษาอังกฤษ — เช่น สุขุมวิท, Chiang Mai…"
                className="h-12 w-full rounded-xl border border-white/15 bg-white/5 pl-11 pr-4 text-sm text-white placeholder:text-white/40 backdrop-blur focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              {["ทั้งหมด", ...regions].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRegion(r)}
                  className={`min-h-11 rounded-xl border px-3 py-2 text-xs font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
                    region === r
                      ? "border-gold bg-gold text-navy-deep"
                      : "border-white/15 bg-white/5 text-white hover:bg-white/10"
                  }`}
                  aria-pressed={region === r}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 text-xs text-white/80">
            พบ <strong className="text-gold">{filtered.length}</strong> พื้นที่
          </div>
        </div>
      </section>

      {/* Near Me */}
      <section className="px-4 pt-10 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <NearMeWidget />
        </div>
      </section>

      {/* Results */}
      <section className="px-4 py-12 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-6xl space-y-10">
          {grouped.length === 0 && (
            <p className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-sm text-white/70">
              ไม่พบพื้นที่ที่ตรงกับการค้นหา — ลองเปลี่ยนคำค้นหรือเลือกภาคอื่น
            </p>
          )}
          {grouped.map(([reg, items]) => (
            <div key={reg}>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-gold sm:text-xl">
                <MapPin className="h-5 w-5" aria-hidden="true" /> {reg}
                <span className="text-xs font-medium text-white/80">({items.length})</span>
              </h2>
              <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((l) => (
                  <li key={l.slug}>
                    <Link
                      to="/locations/$slug"
                      params={{ slug: l.slug }}
                      className="group flex h-full flex-col gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur transition hover:-translate-y-0.5 hover:border-gold/40 hover:bg-white/[0.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gold/80">
                          <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                          {l.type === "headquarters" ? "สำนักงานใหญ่" : l.type === "branch" ? "สาขา" : l.type === "bkk-district" ? "ตัวแทน กทม." : l.type === "transit" ? "BTS/MRT" : l.type === "mall" ? "ห้างสรรพสินค้า" : l.type === "expat-hub" ? "ย่านเอ็กซ์แพท" : "ตัวแทนจังหวัด"}
                        </div>
                        <ArrowRight className="h-4 w-4 text-white/40 transition group-hover:translate-x-1 group-hover:text-gold" aria-hidden="true" />
                      </div>
                      <div className="font-bold text-white">{l.nameTh}</div>
                      <div className="text-[11px] text-white/80">{l.nameEn}</div>
                      {l.address && <div className="text-[11px] text-white/70">{l.address}</div>}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-navy-deep px-4 py-12 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <h2 className="text-xl font-bold sm:text-2xl">ต้องการบริการในพื้นที่ของคุณ?</h2>
            <p className="mt-1 text-sm text-white/70">ติดต่อทีมงานเพื่อรับใบเสนอราคาฟรีภายใน 15 นาที</p>
          </div>
          <a
            href="tel:0832494999"
            className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-gold px-5 py-3 text-sm font-bold text-navy-deep transition hover:bg-gold/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
          >
            <Phone className="h-4 w-4" aria-hidden="true" /> โทร 083-249-4999
          </a>
        </div>
      </section>
          <SiteCoreLinks />
    </main>
  );
}
